import React, { useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";
import { captureAttribution, autoLoadMetaPixelOnIdle } from "../lib/tracking";
import { installOfflineReplay } from "../lib/leadSubmit";
import {
  startEventBus,
  track,
  setRouteEventId,
} from "../lib/eventBus";
import {
  installAutoInstrumentation,
  teardownAutoInstrumentation,
} from "../lib/instrument";
import { newEventId } from "../lib/tracking";

/**
 * Shared shell ; header + outlet + footer + cookie banner.
 * Also bootstraps the telemetry pipeline (event bus + auto-instrumentation)
 * and the Meta Pixel.
 */
export default function SiteShell({ children }) {
  // No client-side routing: every page is a full load, so location is fixed for this mount.
  const { pathname, search } = window.location;

  // One-time global setup
  useEffect(() => {
    captureAttribution();
    installOfflineReplay();
    autoLoadMetaPixelOnIdle();
    startEventBus();
    installAutoInstrumentation();

    // ── LID backdoor ──────────────────────────────────────────────────────
    // Typing L → I → D on any public page (outside an input) navigates to
    // the admin login. The sequence is case-insensitive and resets after
    // 2 s of inactivity or on any unrecognised key.
    const SECRET = "lid";
    const ADMIN_URL = `${window.location.origin}/admin`;

    let buf      = "";
    let idleTimer = null;

    const resetBuf = () => { buf = ""; };

    const onKeyDown = (e) => {
      // Don't intercept while user is typing in a form field
      const tag = (e.target || {}).tagName || "";
      if (
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" ||
        (e.target || {}).isContentEditable ||
        e.ctrlKey || e.altKey || e.metaKey ||
        e.key.length !== 1          // ignore Shift, Enter, Arrow keys, etc.
      ) {
        resetBuf();
        return;
      }

      clearTimeout(idleTimer);
      buf = (buf + e.key.toLowerCase()).slice(-SECRET.length);

      if (buf === SECRET) {
        buf = "";
        // Plant a session flag so AdminDashboard knows this tab
        // arrived through the correct sequence, not a direct URL.
        try { sessionStorage.setItem("zenith.lid", "1"); } catch {}
        window.location.href = ADMIN_URL;
        return;
      }

      // Clear buffer if the character can't be part of the sequence
      // (avoids false positives from random typing)
      if (!SECRET.startsWith(buf)) resetBuf();

      idleTimer = setTimeout(resetBuf, 2000);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      teardownAutoInstrumentation();
      window.removeEventListener("keydown", onKeyDown);
      clearTimeout(idleTimer);
    };
  }, []);

  // Per-route: fresh route_event_id, scroll-top, page_view event (mirrored to Pixel)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    const routeId = newEventId();
    setRouteEventId(routeId);
    track("page_view", { path: pathname, search }); // auto-mirrors to Meta PageView

    // Re-scan for any [data-track-section] elements on the new route.
    // We do it after a microtask so the new page has rendered.
    const t = setTimeout(() => {
      // Re-install instrumentation: cheap (idempotent); section observer
      // is created fresh inside installAutoInstrumentation only when needed.
      // For per-route section watching we use a dedicated lightweight observer:
      const sections = document.querySelectorAll("[data-track-section]");
      const seen = new WeakSet();
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting || seen.has(e.target)) return;
            seen.add(e.target);
            const name =
              e.target.getAttribute("data-track-section") ||
              e.target.getAttribute("data-testid") ||
              "section";
            const mirror = e.target.getAttribute("data-track-as") === "ViewContent";
            track(mirror ? "view_content" : "section_view", {
              section: name,
              intersectionRatio: Number(e.intersectionRatio.toFixed(2)),
            });
          });
        },
        { threshold: 0.4 }
      );
      sections.forEach((el) => obs.observe(el));
      // Cleanup runs when this effect re-fires for the next route
      cleanupRef.current = () => obs.disconnect();
    }, 50);
    return () => {
      clearTimeout(t);
      try {
        cleanupRef.current && cleanupRef.current();
      } catch {
        /* ignore */
      }
    };
  }, [pathname, search]);

  return (
    <>
      <Header />
      <main style={{ position: "relative", zIndex: 2 }}>
        {children}
      </main>
      <Footer />
    </>
  );
}

// Module-level ref for the per-route observer cleanup
const cleanupRef = { current: null };
